/**
 * Integrytas Group — FacetFilter (common).
 *
 * A lightweight toolbar facet: a labelled multi-select popover for filtering a list
 * by a logical field (e.g. Customer Type, Status, Currency). Every list page should
 * offer field facets in addition to the free-text filter — a derived-app default
 * (CLAUDE.md §17.B5). Compose one per filterable column; the page combines the
 * selected values into its `filtered` memo.
 *
 * Controlled: `selected` is an array of option values; `onChange` returns the new set.
 */

import { useEffect, useRef, useState } from 'react';

export interface FacetOption {
  value: string;
  label: string;
  /** Optional result count for this value (P-09). Pass the number of rows
      the option would match so filtering becomes navigation, not trial and
      error. Omit to render a plain option. */
  count?: number;
}

export interface FacetFilterProps {
  label: string;
  options: FacetOption[];
  selected: string[];
  onChange: (values: string[]) => void;
}

export function FacetFilter({ label, options, selected, onChange }: FacetFilterProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className={
          'flex h-8 items-center gap-1.5 rounded-csm-control border px-2.5 text-csm-sm ' +
          (selected.length
            ? 'border-primary bg-primary-soft text-primary'
            : 'border-csm-border bg-white text-csm-ink-soft hover:bg-csm-surface-alt')
        }>
        {label}
        {selected.length > 0 && (
          <span className="rounded-full bg-primary px-1.5 text-csm-2xs text-white">{selected.length}</span>
        )}
        <span className="text-csm-2xs">▾</span>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 max-h-72 w-56 overflow-auto rounded-csm-card border border-csm-border bg-white p-1 elev-1 anim-pop">
          {selected.length > 0 && (
            <button type="button" onClick={() => onChange([])}
              className="mb-1 w-full rounded px-2 py-1 text-left text-csm-xs text-csm-danger hover:bg-csm-surface-alt">
              Clear ({selected.length})
            </button>
          )}
          {options.length === 0 && (
            <div className="px-2 py-1 text-csm-xs italic text-csm-watermark">No options</div>
          )}
          {options.map((o) => (
            <label key={o.value}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-csm-sm hover:bg-csm-surface-alt">
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)}
                className="h-3.5 w-3.5 accent-gray-700" />
              <span className="min-w-0 flex-1 truncate">{o.label}</span>
              {o.count !== undefined && (
                <span className="csm-num shrink-0 text-csm-2xs text-csm-muted">{o.count}</span>
              )}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
